export const TRIP_CANCELLATION_CODES = {
    PROCESSED: 'TRIP_CANCELLATIONS_PROCESSED',
    NO_TRIPS: 'NO_TRIPS_TO_CANCEL',
    INVALID: 'INVALID_REQUEST',
    INTERNAL: 'INTERNAL_SERVER_ERROR',
};

export const TRIP_CANCELLATION_ROW_STATUS = {
    PROCESSED: 'processed',
    NOT_FOUND: 'not_found',
    ERROR: 'error',
    SKIPPED_ALREADY_CANCELLED: 'skipped_already_cancelled',
};

export const TRIP_CANCELLATION_PARTNERS = {
    STANDARD: 'standard',
    FLOWER: 'flower',
    ALOA: 'aloa',
    ODALYS: 'odalys',
};

const FILE_EXTENSIONS = {
    standard: /\.(xls|xlsx)$/i,
    flower: /\.(xls|xlsx)$/i,
    aloa: /\.(xls|xlsx)$/i,
    odalys: /\.(xls|xlsx|csv)$/i,
};

const mapPolicy = (policy) => ({
    policyId: policy.policy_id ?? '',
    policyCode: policy.policy_code ?? '',
    status: policy.status ?? '',
    tripCancellationDate: policy.trip_cancellation_date ?? null,
    updatedClaimsCount: policy.updated_claims_count ?? 0,
    emailQueued: policy.email_queued === true,
    claims: Array.isArray(policy.claims)
        ? policy.claims.map((claim) => ({
            claimId: claim.claim_id ?? '',
            tripDay: claim.trip_day ?? null,
            previousStatus: claim.previous_status ?? '',
            newStatus: claim.new_status ?? '',
            claimStage: claim.claim_stage ?? '',
        }))
        : [],
});

const dedupePolicies = (policies) => {
    const seen = new Set();
    const unique = [];
    policies.forEach((policy) => {
        const key = `${policy.policyId || ''}|${policy.policyCode || ''}|${policy.tripCancellationDate || ''}`;
        if (seen.has(key)) return;
        seen.add(key);
        unique.push(policy);
    });
    return unique;
};

const mapDetailRow = (row) => {
    const policies = dedupePolicies(
        (Array.isArray(row.policies) ? row.policies : []).map(mapPolicy)
    );
    return {
        reservationNumber: String(row.reservation_number ?? ''),
        status: row.status ?? '',
        message: row.message ?? null,
        clientInfo: row.client_info
            ? {
                firstName: row.client_info.first_name ?? '',
                lastName: row.client_info.last_name ?? '',
                email: row.client_info.email ?? '',
            }
            : null,
        policies,
    };
};

/** Format structuré historique (`code` + `details` + `summary`). */
const isLegacyTripCancellationResponse = (result) => (
    result != null
    && typeof result === 'object'
    && typeof result.code === 'string'
);

/** Format Flower / backup : `processed_results` + compteurs. */
export const isProcessedResultsCancellationResponse = (result) => (
    result != null
    && typeof result === 'object'
    && (
        Array.isArray(result.processed_results)
        || (
            result.success === true
            && (
                result.total_trips_processed != null
                || result.successful_processing != null
                || result.processed_at != null
            )
        )
    )
);

export const isTripCancellationResponse = (result) => (
    isLegacyTripCancellationResponse(result)
    || isProcessedResultsCancellationResponse(result)
);

export const validateTripCancellationFile = (file, partner = TRIP_CANCELLATION_PARTNERS.FLOWER) => {
    if (!file) return { valid: false, reason: 'no_file' };
    const pattern = FILE_EXTENSIONS[partner] || FILE_EXTENSIONS.flower;
    if (!pattern.test(file.name)) {
        return {
            valid: false,
            reason: partner === TRIP_CANCELLATION_PARTNERS.ODALYS
                ? 'invalid_extension_odalys'
                : 'invalid_extension',
        };
    }
    return { valid: true };
};

export const getTripCancellationStatusFilters = () => [
    'all',
    TRIP_CANCELLATION_ROW_STATUS.PROCESSED,
    TRIP_CANCELLATION_ROW_STATUS.SKIPPED_ALREADY_CANCELLED,
    TRIP_CANCELLATION_ROW_STATUS.NOT_FOUND,
    TRIP_CANCELLATION_ROW_STATUS.ERROR,
];

const normalizeLegacyResult = (result) => {
    const summary = result.summary || {};
    const filters = result.filters || {};
    const details = Array.isArray(result.details) ? result.details.map(mapDetailRow) : [];

    return {
        success: result.success === true,
        code: result.code,
        message: result.message || '',
        timestamp: result.timestamp || null,
        summary: {
            totalRows: summary.total_rows ?? 0,
            rowsProcessed: summary.rows_processed ?? 0,
            rowsNotFound: summary.rows_not_found ?? 0,
            rowsSkipped: summary.rows_skipped ?? 0,
            rowsFailed: summary.rows_failed ?? 0,
            policiesCancelled: summary.policies_cancelled ?? 0,
            claimsRejected: summary.claims_rejected ?? 0,
            emailsQueued: summary.emails_queued ?? 0,
        },
        filters: {
            filename: filters.filename ?? null,
            partner: filters.partner ?? null,
        },
        details,
        error: result.error || null,
        sourceFormat: 'legacy',
    };
};

const normalizeProcessedResultsFormat = (result) => {
    const processedResults = Array.isArray(result.processed_results) ? result.processed_results : [];
    const details = processedResults.map(mapDetailRow);

    let policiesCancelled = 0;
    let claimsRejected = 0;
    details.forEach((row) => {
        policiesCancelled += row.policies.length;
        row.policies.forEach((policy) => {
            claimsRejected += Number(policy.updatedClaimsCount) || 0;
        });
    });

    const totalRows = result.total_trips_processed ?? details.length;
    const rowsProcessed = result.successful_processing
        ?? details.filter((row) => row.status === TRIP_CANCELLATION_ROW_STATUS.PROCESSED).length;
    const rowsFailed = Math.max(0, Number(totalRows) - Number(rowsProcessed));

    const success = result.success === true;
    const code = success
        ? (details.length === 0 ? TRIP_CANCELLATION_CODES.NO_TRIPS : TRIP_CANCELLATION_CODES.PROCESSED)
        : (result.code || TRIP_CANCELLATION_CODES.INTERNAL);

    const message = result.message
        || (success
            ? `${rowsProcessed} séjour(s) traité(s), ${policiesCancelled} police(s) annulée(s).`
            : 'Erreur lors du traitement des annulations.');

    return {
        success,
        code,
        message,
        timestamp: result.processed_at || result.timestamp || null,
        summary: {
            totalRows,
            rowsProcessed,
            rowsNotFound: result.rows_not_found ?? 0,
            rowsSkipped: result.rows_skipped ?? 0,
            rowsFailed,
            policiesCancelled,
            claimsRejected,
            emailsQueued: result.emails_queued ?? 0,
        },
        filters: {
            filename: result.filename ?? result.filters?.filename ?? null,
            partner: result.partner ?? result.filters?.partner ?? null,
        },
        details,
        error: result.error || null,
        sourceFormat: 'processed_results',
    };
};

export const normalizeTripCancellationResult = (result) => {
    if (!isTripCancellationResponse(result)) return null;
    if (isLegacyTripCancellationResponse(result)) {
        return normalizeLegacyResult(result);
    }
    return normalizeProcessedResultsFormat(result);
};

export const isTripCancellationPartialSuccess = (normalized, partner) => {
    if (!normalized?.summary) return false;
    const { rowsProcessed, rowsFailed, rowsNotFound, rowsSkipped } = normalized.summary;
    if (rowsProcessed > 0 && (rowsFailed > 0 || rowsNotFound > 0)) return true;
    if (partner === TRIP_CANCELLATION_PARTNERS.ALOA && rowsProcessed > 0 && rowsSkipped > 0) {
        return true;
    }
    return false;
};

export const getTripCancellationAlertVariant = (normalized, partner) => {
    if (!normalized) return 'secondary';
    switch (normalized.code) {
        case TRIP_CANCELLATION_CODES.PROCESSED:
            return isTripCancellationPartialSuccess(normalized, partner) ? 'warning' : 'success';
        case TRIP_CANCELLATION_CODES.NO_TRIPS:
            return 'info';
        case TRIP_CANCELLATION_CODES.INVALID:
            return 'warning';
        default:
            return 'danger';
    }
};

export const shouldShowTripCancellationSummary = (normalized) => (
    normalized != null
    && (
        normalized.code === TRIP_CANCELLATION_CODES.PROCESSED
        || normalized.code === TRIP_CANCELLATION_CODES.NO_TRIPS
        || (normalized.code === TRIP_CANCELLATION_CODES.INTERNAL && normalized.summary != null)
    )
);

export const getTripCancellationFieldError = (result) => {
    if (!result || result.code !== TRIP_CANCELLATION_CODES.INVALID) return null;
    const field = result.error?.field;
    const reason = result.error?.reason;
    if (!field || !reason) return null;
    return {
        field,
        reason,
        missingColumns: Array.isArray(result.error?.missing_columns)
            ? result.error.missing_columns
            : [],
    };
};

export const parseTripCancellationApiError = (error) => {
    const data = error?.response?.data;
    if (isTripCancellationResponse(data)) return data;
    return null;
};

export const filterTripCancellationDetails = (details, statusFilter, search) => {
    let rows = details;
    if (statusFilter && statusFilter !== 'all') {
        rows = rows.filter((row) => row.status === statusFilter);
    }
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => {
        const clientName = row.clientInfo
            ? `${row.clientInfo.firstName} ${row.clientInfo.lastName}`.toLowerCase()
            : '';
        const email = row.clientInfo?.email?.toLowerCase() || '';
        const policyMatch = row.policies.some((policy) => (
            String(policy.policyCode || '').toLowerCase().includes(query)
        ));
        return (
            row.reservationNumber.toLowerCase().includes(query)
            || clientName.includes(query)
            || email.includes(query)
            || policyMatch
            || (row.message && row.message.toLowerCase().includes(query))
        );
    });
};

/** Une ligne par police (plat), pour tableau + export Excel. */
export const flattenTripCancellationPolicyRows = (details) => {
    if (!Array.isArray(details) || details.length === 0) return [];

    return details.flatMap((row) => {
        const clientLabel = row.clientInfo
            ? `${row.clientInfo.firstName} ${row.clientInfo.lastName}`.trim()
            : '';
        const base = {
            reservationNumber: row.reservationNumber || '',
            status: row.status || '',
            clientLabel: clientLabel || '—',
            clientEmail: row.clientInfo?.email || '',
            message: row.message || '',
        };

        if (!row.policies.length) {
            return [{
                ...base,
                policyId: '',
                policyCode: '',
                policyStatus: '',
                cancellationDate: null,
                updatedClaimsCount: 0,
                claimsCount: 0,
            }];
        }

        return row.policies.map((policy) => ({
            ...base,
            policyId: policy.policyId || '',
            policyCode: policy.policyCode || '',
            policyStatus: policy.status || '',
            cancellationDate: policy.tripCancellationDate || null,
            updatedClaimsCount: policy.updatedClaimsCount ?? 0,
            claimsCount: Array.isArray(policy.claims) ? policy.claims.length : 0,
        }));
    });
};

export const filterTripCancellationPolicyRows = (policyRows, statusFilter, search) => {
    let rows = policyRows;
    if (statusFilter && statusFilter !== 'all') {
        rows = rows.filter((row) => row.status === statusFilter);
    }
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => (
        String(row.reservationNumber).toLowerCase().includes(query)
        || String(row.clientLabel).toLowerCase().includes(query)
        || String(row.clientEmail).toLowerCase().includes(query)
        || String(row.policyCode).toLowerCase().includes(query)
        || String(row.policyStatus).toLowerCase().includes(query)
        || String(row.message).toLowerCase().includes(query)
    ));
};

export const paginateTripCancellationDetails = (rows, page, pageSize) => {
    const total = rows.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const start = (safePage - 1) * pageSize;
    return {
        rows: rows.slice(start, start + pageSize),
        total,
        totalPages,
        page: safePage,
    };
};

export const buildTripCancellationStatusCounts = (details) => {
    const counts = { all: details.length };
    getTripCancellationStatusFilters().forEach((key) => {
        if (key !== 'all') {
            counts[key] = details.filter((row) => row.status === key).length;
        }
    });
    return counts;
};

export const formatTripCancellationDateTime = (value) => {
    if (value == null || value === '') return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString('fr-FR', { timeZone: 'Europe/Paris' });
};

export const buildTripCancellationExcelRows = (policyRows, {
    labels,
    translateStatus,
}) => {
    if (!Array.isArray(policyRows) || policyRows.length === 0) return [];

    return policyRows.map((row) => ({
        [labels.reservationNumber]: row.reservationNumber || '',
        [labels.client]: row.clientLabel === '—' ? '' : (row.clientLabel || ''),
        [labels.policyCode]: row.policyCode || '',
        [labels.policyStatus]: row.policyStatus || '',
        [labels.cancellationDate]: row.cancellationDate
            ? formatTripCancellationDateTime(row.cancellationDate)
            : '',
        [labels.claimsUpdated]: row.updatedClaimsCount ?? 0,
        [labels.status]: translateStatus(row.status),
        [labels.message]: row.message || '',
    }));
};
